/**
 * Foundry Upload — storefront upload widget for 25M Printing (Empire theme).
 * Replaces the Upload-Lift product-page upload block.
 *
 * - Vanilla JS, no jQuery. Namespaced under window.FoundryUpload / .fu- CSS.
 * - Talks to the Foundry public uploads API ({FOUNDRY_BASE}/api/public/uploads).
 * - On-demand external libs (cdnjs only): pdf.js 3.11.174, Cropper.js 1.6.2.
 * - Never breaks the product page: every network call is wrapped; if the
 *   fields endpoint fails the widget renders nothing.
 *
 * Product form discovery (fallback chain, documented in INSTALL.md):
 *   1. container.closest('form[action*="/cart/add"]')      — snippet placed inside the form (intended)
 *   2. document.querySelector('form[action^="/cart/add"]') — Empire's product form action
 *   3. document.querySelector('form[action*="/cart/add"]') — locale-prefixed actions (/en/cart/add)
 *   4. document.querySelector('form[data-product-form], form.product-form')
 */
(function () {
  'use strict';

  if (window.FoundryUpload && window.FoundryUpload.__loaded) return;

  /* ------------------------------------------------------------------ *
   * Config
   * ------------------------------------------------------------------ */

  var FOUNDRY_BASE = String(window.FOUNDRY_UPLOAD_BASE || 'https://foundry.dp7.app').replace(/\/+$/, '');
  var API_BASE = FOUNDRY_BASE + '/api/public/uploads';

  var PDFJS_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
  var PDFJS_WORKER_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  var CROPPER_JS_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/cropperjs/1.6.2/cropper.min.js';
  var CROPPER_CSS_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/cropperjs/1.6.2/cropper.min.css';

  var THUMB_MAX_PX = 640; // max edge for generated PDF preview canvases

  /* ------------------------------------------------------------------ *
   * Small helpers
   * ------------------------------------------------------------------ */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text; // always textContent — never innerHTML with user data
    return node;
  }

  function formatBytes(n) {
    if (!n && n !== 0) return '';
    if (n < 1024) return n + ' B';
    if (n < 1024 * 1024) return (n / 1024).toFixed(n < 10240 ? 1 : 0) + ' KB';
    if (n < 1024 * 1024 * 1024) return (n / (1024 * 1024)).toFixed(1) + ' MB';
    return (n / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  }

  function fileExt(name) {
    var m = /\.([a-z0-9]+)(?:\?.*)?$/i.exec(String(name || ''));
    return m ? m[1].toLowerCase() : '';
  }

  function baseName(name) {
    return String(name || 'file').replace(/\.[a-z0-9]+$/i, '');
  }

  function apiJson(url, options) {
    options = options || {};
    if (!options.headers) options.headers = {};
    options.headers['Accept'] = 'application/json';
    return window.fetch(url, options).then(function (res) {
      if (!res.ok) {
        var err = new Error('HTTP ' + res.status);
        err.status = res.status;
        throw err;
      }
      return res.json();
    });
  }

  function readAsArrayBuffer(blob) {
    if (blob.arrayBuffer) return blob.arrayBuffer();
    return new Promise(function (resolve, reject) {
      var r = new FileReader();
      r.onload = function () { resolve(r.result); };
      r.onerror = function () { reject(r.error || new Error('read error')); };
      r.readAsArrayBuffer(blob);
    });
  }

  function canvasToBlob(canvas, type, quality) {
    return new Promise(function (resolve) {
      if (canvas.toBlob) {
        canvas.toBlob(function (blob) { resolve(blob); }, type, quality);
      } else {
        resolve(null);
      }
    });
  }

  /* On-demand external assets (cached promises, retryable on failure) */
  var externalCache = {};

  function loadScript(src) {
    if (externalCache[src]) return externalCache[src];
    externalCache[src] = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () { resolve(); };
      s.onerror = function () {
        delete externalCache[src];
        reject(new Error('Failed to load ' + src));
      };
      document.head.appendChild(s);
    });
    return externalCache[src];
  }

  function loadStyle(href) {
    if (externalCache[href]) return externalCache[href];
    externalCache[href] = new Promise(function (resolve) {
      var l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = href;
      l.onload = function () { resolve(); };
      l.onerror = function () { resolve(); }; // missing CSS degrades style only, never block
      document.head.appendChild(l);
    });
    return externalCache[href];
  }

  function loadPdfJs() {
    return loadScript(PDFJS_SRC).then(function () {
      var lib = window['pdfjs-dist/build/pdf'] || window.pdfjsLib;
      if (!lib) throw new Error('pdf.js unavailable');
      if (lib.GlobalWorkerOptions && !lib.GlobalWorkerOptions.workerSrc) {
        lib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;
      }
      return lib;
    });
  }

  function loadCropper() {
    return Promise.all([loadScript(CROPPER_JS_SRC), loadStyle(CROPPER_CSS_SRC)]).then(function () {
      if (!window.Cropper) throw new Error('Cropper.js unavailable');
      return window.Cropper;
    });
  }

  /* ------------------------------------------------------------------ *
   * File-type helpers / validation
   * ------------------------------------------------------------------ */

  var IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'avif'];

  function isImageFile(file) {
    var mime = (file.type || '').toLowerCase();
    if (mime.indexOf('image/') === 0) return true;
    return IMAGE_EXTS.indexOf(fileExt(file.name)) !== -1;
  }

  function isPdfLike(file) {
    var mime = (file.type || '').toLowerCase();
    var ext = fileExt(file.name);
    return mime === 'application/pdf' || ext === 'pdf' || ext === 'ai' ||
      mime === 'application/postscript' || mime === 'application/illustrator';
  }

  function typeAccepted(field, file) {
    var accepted = field.acceptedTypes || [];
    if (!accepted.length) return true;
    var ext = fileExt(file.name);
    var mime = (file.type || '').toLowerCase();
    return accepted.some(function (t) {
      t = String(t || '').toLowerCase().trim();
      if (!t) return false;
      if (t.indexOf('/') !== -1) {
        if (t.slice(-2) === '/*') return mime.indexOf(t.slice(0, -1)) === 0;
        return mime === t;
      }
      return t.replace(/^\./, '') === ext;
    });
  }

  function acceptAttr(field) {
    var accepted = field.acceptedTypes || [];
    if (!accepted.length) return '';
    return accepted
      .map(function (t) {
        t = String(t || '').trim();
        if (!t) return '';
        if (t.indexOf('/') !== -1) return t;          // mime type
        return t.charAt(0) === '.' ? t : '.' + t;     // extension
      })
      .filter(Boolean)
      .join(',');
  }

  function validateFile(field, file) {
    if (!typeAccepted(field, file)) {
      var types = (field.acceptedTypes || []).join(', ');
      return 'File type not accepted' + (types ? ' (allowed: ' + types + ')' : '') + '.';
    }
    if (field.maxSizeBytes && file.size > field.maxSizeBytes) {
      return 'File is too large (' + formatBytes(file.size) + '). Max ' + formatBytes(field.maxSizeBytes) + '.';
    }
    if (field.minSizeBytes && file.size < field.minSizeBytes) {
      return 'File is too small (' + formatBytes(file.size) + '). Min ' + formatBytes(field.minSizeBytes) + '.';
    }
    return null;
  }

  function validateDimensions(field, dims) {
    if (!dims) return null; // couldn't decode — let the server decide
    if (field.minWidth && dims.width < field.minWidth) {
      return 'Image is ' + dims.width + 'px wide; needs at least ' + field.minWidth + 'px.';
    }
    if (field.minHeight && dims.height < field.minHeight) {
      return 'Image is ' + dims.height + 'px tall; needs at least ' + field.minHeight + 'px.';
    }
    if (field.maxWidth && dims.width > field.maxWidth) {
      return 'Image is ' + dims.width + 'px wide; max is ' + field.maxWidth + 'px.';
    }
    if (field.maxHeight && dims.height > field.maxHeight) {
      return 'Image is ' + dims.height + 'px tall; max is ' + field.maxHeight + 'px.';
    }
    return null;
  }

  function getImageDimensions(blob) {
    return new Promise(function (resolve) {
      var url;
      try { url = URL.createObjectURL(blob); } catch (e) { return resolve(null); }
      var img = new Image();
      img.onload = function () {
        var d = { width: img.naturalWidth, height: img.naturalHeight };
        URL.revokeObjectURL(url);
        resolve(d.width > 0 ? d : null);
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });
  }

  /* ------------------------------------------------------------------ *
   * PDF / AI preview (page 1 -> canvas -> thumbnail + PNG preview blob)
   * ------------------------------------------------------------------ */

  function renderPdfPreview(file) {
    return loadPdfJs()
      .then(function (pdfjs) {
        return readAsArrayBuffer(file).then(function (buf) {
          return pdfjs.getDocument({ data: buf }).promise;
        });
      })
      .then(function (doc) { return doc.getPage(1); })
      .then(function (page) {
        var vp = page.getViewport({ scale: 1 });
        var scale = THUMB_MAX_PX / Math.max(vp.width, vp.height);
        if (!isFinite(scale) || scale <= 0) scale = 1;
        if (scale > 2) scale = 2;
        vp = page.getViewport({ scale: scale });
        var canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.ceil(vp.width));
        canvas.height = Math.max(1, Math.ceil(vp.height));
        var ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        return page.render({ canvasContext: ctx, viewport: vp }).promise.then(function () {
          return canvasToBlob(canvas, 'image/png').then(function (blob) {
            return { thumbUrl: canvas.toDataURL('image/png'), previewBlob: blob };
          });
        });
      });
  }

  /* ------------------------------------------------------------------ *
   * Cropper modal
   * ------------------------------------------------------------------ */

  function parseRatio(r) {
    if (!r || String(r).toLowerCase() === 'free') return NaN;
    var parts = String(r).split(':');
    var a = parseFloat(parts[0]);
    var b = parseFloat(parts[1]);
    if (!a || !b) return NaN;
    return a / b;
  }

  /**
   * Opens a crop modal for an image file.
   * Resolves with:
   *   { action: 'apply', blob, filename }  — upload the cropped blob
   *   { action: 'skip' }                   — upload the original
   *   { action: 'cancel' }                 — do not upload at all
   * If Cropper.js fails to load, resolves with { action: 'skip' }.
   */
  function openCropModal(field, file) {
    return loadCropper()
      .then(function (Cropper) {
        return new Promise(function (resolve) {
          var isCircle = field.cropShape === 'circle';
          var ratios = (field.cropRatios && field.cropRatios.length) ? field.cropRatios.slice() : ['free'];
          if (isCircle && ratios.indexOf('1:1') === -1 && ratios.indexOf('free') === -1) ratios.unshift('1:1');

          var objectUrl = URL.createObjectURL(file);
          var prevOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';

          var overlay = el('div', 'fu-modal-overlay' + (isCircle ? ' fu-crop-circle' : ''));
          overlay.setAttribute('role', 'dialog');
          overlay.setAttribute('aria-modal', 'true');
          overlay.setAttribute('aria-label', 'Crop image');

          var modal = el('div', 'fu-modal');
          var header = el('div', 'fu-modal-header');
          header.appendChild(el('span', 'fu-modal-title', 'Crop your image'));
          var closeBtn = el('button', 'fu-modal-close', '×');
          closeBtn.type = 'button';
          closeBtn.setAttribute('aria-label', 'Cancel crop');
          header.appendChild(closeBtn);
          modal.appendChild(header);

          var stage = el('div', 'fu-crop-stage');
          var img = document.createElement('img');
          img.className = 'fu-crop-img';
          img.alt = '';
          img.src = objectUrl;
          stage.appendChild(img);
          modal.appendChild(stage);

          var controls = el('div', 'fu-crop-controls');
          var ratioWrap = el('div', 'fu-crop-ratios');
          var ratioBtns = [];
          ratios.forEach(function (r, i) {
            var label = String(r).toLowerCase() === 'free' ? 'Free' : String(r);
            var b = el('button', 'fu-btn fu-btn-ratio' + (i === 0 ? ' fu-active' : ''), label);
            b.type = 'button';
            b.setAttribute('data-ratio', String(r));
            ratioWrap.appendChild(b);
            ratioBtns.push(b);
          });
          controls.appendChild(ratioWrap);

          var rotateWrap = el('div', 'fu-crop-rotate');
          var rotL = el('button', 'fu-btn', '↺ 90°');
          rotL.type = 'button';
          rotL.setAttribute('aria-label', 'Rotate left');
          var rotR = el('button', 'fu-btn', '↻ 90°');
          rotR.type = 'button';
          rotR.setAttribute('aria-label', 'Rotate right');
          rotateWrap.appendChild(rotL);
          rotateWrap.appendChild(rotR);
          controls.appendChild(rotateWrap);
          modal.appendChild(controls);

          var footer = el('div', 'fu-modal-footer');
          var cancelBtn = el('button', 'fu-btn fu-btn-ghost', 'Cancel');
          cancelBtn.type = 'button';
          var skipBtn = el('button', 'fu-btn fu-btn-ghost', 'Skip crop');
          skipBtn.type = 'button';
          var applyBtn = el('button', 'fu-btn fu-btn-primary', 'Apply crop');
          applyBtn.type = 'button';
          footer.appendChild(cancelBtn);
          footer.appendChild(skipBtn);
          footer.appendChild(applyBtn);
          modal.appendChild(footer);

          overlay.appendChild(modal);
          document.body.appendChild(overlay);

          var cropper = null;
          var settled = false;

          function cleanup() {
            if (cropper) { try { cropper.destroy(); } catch (e) { /* noop */ } }
            URL.revokeObjectURL(objectUrl);
            document.body.style.overflow = prevOverflow;
            document.removeEventListener('keydown', onKey);
            if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
          }

          function settle(result) {
            if (settled) return;
            settled = true;
            cleanup();
            resolve(result);
          }

          function onKey(e) {
            if (e.key === 'Escape') settle({ action: 'cancel' });
          }
          document.addEventListener('keydown', onKey);

          img.onload = function () {
            try {
              cropper = new Cropper(img, {
                aspectRatio: parseRatio(ratios[0]),
                viewMode: 1,
                autoCropArea: 0.9,
                background: false,
                responsive: true,
                checkOrientation: true
              });
            } catch (e) {
              settle({ action: 'skip' });
            }
          };
          img.onerror = function () { settle({ action: 'skip' }); };

          ratioBtns.forEach(function (b) {
            b.addEventListener('click', function () {
              ratioBtns.forEach(function (o) { o.classList.remove('fu-active'); });
              b.classList.add('fu-active');
              if (cropper) cropper.setAspectRatio(parseRatio(b.getAttribute('data-ratio')));
            });
          });
          rotL.addEventListener('click', function () { if (cropper) cropper.rotate(-90); });
          rotR.addEventListener('click', function () { if (cropper) cropper.rotate(90); });
          closeBtn.addEventListener('click', function () { settle({ action: 'cancel' }); });
          cancelBtn.addEventListener('click', function () { settle({ action: 'cancel' }); });
          skipBtn.addEventListener('click', function () { settle({ action: 'skip' }); });
          overlay.addEventListener('click', function (e) {
            if (e.target === overlay) settle({ action: 'cancel' });
          });

          applyBtn.addEventListener('click', function () {
            if (!cropper) return settle({ action: 'skip' });
            var canvas;
            try {
              canvas = cropper.getCroppedCanvas({ imageSmoothingQuality: 'high' });
            } catch (e) {
              return settle({ action: 'skip' });
            }
            if (!canvas) return settle({ action: 'skip' });

            if (isCircle) {
              var c2 = document.createElement('canvas');
              c2.width = canvas.width;
              c2.height = canvas.height;
              var ctx = c2.getContext('2d');
              ctx.beginPath();
              ctx.ellipse(c2.width / 2, c2.height / 2, c2.width / 2, c2.height / 2, 0, 0, Math.PI * 2);
              ctx.closePath();
              ctx.clip();
              ctx.drawImage(canvas, 0, 0);
              canvas = c2;
            }

            // PNG for circle (transparency); otherwise keep jpeg/png family of the source
            var srcMime = (file.type || '').toLowerCase();
            var outType = isCircle ? 'image/png' : (srcMime === 'image/png' ? 'image/png' : 'image/jpeg');
            var outExt = outType === 'image/png' ? 'png' : 'jpg';
            var filename = baseName(file.name) + '-cropped.' + outExt;

            applyBtn.disabled = true;
            canvasToBlob(canvas, outType, 0.92).then(function (blob) {
              if (blob) settle({ action: 'apply', blob: blob, filename: filename });
              else settle({ action: 'skip' });
            });
          });
        });
      })
      .catch(function () {
        // Cropper failed to load — never block the upload
        return { action: 'skip' };
      });
  }

  /* ------------------------------------------------------------------ *
   * File card UI
   * ------------------------------------------------------------------ */

  function createFileCard(listEl, filename, sizeBytes) {
    var card = el('div', 'fu-file-card fu-uploading');

    var thumb = el('div', 'fu-thumb');
    var extLabel = el('span', 'fu-thumb-ext', (fileExt(filename) || 'file').toUpperCase());
    thumb.appendChild(extLabel);
    card.appendChild(thumb);

    var meta = el('div', 'fu-file-meta');
    var nameEl = el('div', 'fu-file-name', filename);
    nameEl.title = filename; // title attribute is safe (attribute value, set via property)
    meta.appendChild(nameEl);
    meta.appendChild(el('div', 'fu-file-size', formatBytes(sizeBytes)));
    var progress = el('div', 'fu-progress');
    var bar = el('div', 'fu-progress-bar');
    progress.appendChild(bar);
    meta.appendChild(progress);
    var errEl = el('div', 'fu-file-error');
    meta.appendChild(errEl);
    card.appendChild(meta);

    var removeBtn = el('button', 'fu-remove', '×');
    removeBtn.type = 'button';
    removeBtn.setAttribute('aria-label', 'Remove file');
    card.appendChild(removeBtn);

    listEl.appendChild(card);

    return {
      root: card,
      removeBtn: removeBtn,
      setProgress: function (fraction) {
        bar.style.width = Math.max(0, Math.min(100, Math.round(fraction * 100))) + '%';
      },
      setThumb: function (url) {
        var img = document.createElement('img');
        img.className = 'fu-thumb-img';
        img.alt = '';
        img.src = url;
        thumb.textContent = '';
        thumb.appendChild(img);
      },
      setDone: function () {
        card.classList.remove('fu-uploading');
        card.classList.add('fu-done');
        bar.style.width = '100%';
      },
      setError: function (msg) {
        card.classList.remove('fu-uploading');
        card.classList.add('fu-error');
        errEl.textContent = msg;
      },
      setBusy: function (busy) {
        removeBtn.disabled = !!busy;
      },
      destroy: function () {
        if (card.parentNode) card.parentNode.removeChild(card);
      }
    };
  }

  /* ------------------------------------------------------------------ *
   * Widget
   * ------------------------------------------------------------------ */

  function Widget(container) {
    this.container = container;
    this.productHandle = (container.getAttribute('data-fu-handle')||container.getAttribute('data-product-handle')) || '';
    this.productTitle = (container.getAttribute('data-fu-title')||container.getAttribute('data-product-title')) || '';
    this.productId = (container.getAttribute('data-fu-id')||container.getAttribute('data-product-id')) || '';
    this.form = this.findForm();
    this.token = null;
    this.sessionPromise = null;
    this.files = [];            // committed files: { id, fieldId, name, size }
    this.fieldCounts = {};      // per-field count incl. in-flight uploads
    this.pendingUploads = 0;
    this.queue = Promise.resolve(); // serializes per-file processing (crop modals)
    this.storageKey = 'fu:session:' + this.productHandle;
    this.init();
  }

  Widget.prototype.findForm = function () {
    return (
      this.container.closest('form[action*="/cart/add"]') ||
      document.querySelector('form[action^="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('form[data-product-form], form.product-form') ||
      null
    );
  };

  Widget.prototype.init = function () {
    var self = this;
    apiJson(API_BASE + '/fields?page=product')
      .then(function (data) {
        var fields = (data && data.fields) || [];
        if (!fields.length) return;
        self.render(fields);
      })
      .catch(function () {
        /* fields endpoint unavailable — render nothing, never break the page */
      });
  };

  /* ---------- rendering ---------- */

  Widget.prototype.render = function (fields) {
    var self = this;
    var root = el('div', 'fu-root');
    fields.forEach(function (field) {
      root.appendChild(self.renderField(field));
    });
    this.container.appendChild(root);
  };

  Widget.prototype.renderField = function (field) {
    var self = this;
    var block = el('div', 'fu-field');
    block.setAttribute('data-fu-field', String(field.id));

    if (field.label) block.appendChild(el('div', 'fu-label', field.label));
    if (field.description) block.appendChild(el('div', 'fu-description', field.description));

    var input = document.createElement('input');
    input.type = 'file';
    input.className = 'fu-input';
    input.tabIndex = -1;
    var accept = acceptAttr(field);
    if (accept) input.setAttribute('accept', accept);
    if (field.allowMultiple) input.multiple = true;

    var drop = el('div', 'fu-dropzone');
    drop.setAttribute('role', 'button');
    drop.tabIndex = 0;
    drop.setAttribute('aria-label', 'Upload ' + (field.label || 'file'));
    drop.appendChild(el('div', 'fu-drop-icon', '⬆'));
    drop.appendChild(el('div', 'fu-drop-text', 'Drag & drop your file here, or tap to browse'));
    var hints = [];
    if (field.acceptedTypes && field.acceptedTypes.length) hints.push(field.acceptedTypes.join(', '));
    if (field.maxSizeBytes) hints.push('max ' + formatBytes(field.maxSizeBytes));
    if (field.allowMultiple && field.maxFiles) hints.push('up to ' + field.maxFiles + ' files');
    if (hints.length) drop.appendChild(el('div', 'fu-drop-hint', hints.join(' · ')));
    drop.appendChild(input);

    if (field.helpText) block.appendChild(el('div', 'fu-help', field.helpText));
    block.appendChild(drop);

    var errEl = el('div', 'fu-field-error');
    errEl.setAttribute('role', 'alert');
    block.appendChild(errEl);

    var list = el('div', 'fu-file-list');
    block.appendChild(list);

    var ctx = { field: field, block: block, list: list, errEl: errEl, input: input };

    drop.addEventListener('click', function (e) {
      if (e.target !== input) input.click();
    });
    drop.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        input.click();
      }
    });
    input.addEventListener('change', function () {
      if (input.files && input.files.length) self.handleFiles(ctx, input.files);
      input.value = '';
    });

    ['dragenter', 'dragover'].forEach(function (evt) {
      drop.addEventListener(evt, function (e) {
        e.preventDefault();
        e.stopPropagation();
        drop.classList.add('fu-dragover');
      });
    });
    ['dragleave', 'dragend'].forEach(function (evt) {
      drop.addEventListener(evt, function (e) {
        e.preventDefault();
        drop.classList.remove('fu-dragover');
      });
    });
    drop.addEventListener('drop', function (e) {
      e.preventDefault();
      e.stopPropagation();
      drop.classList.remove('fu-dragover');
      if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) {
        self.handleFiles(ctx, e.dataTransfer.files);
      }
    });

    return block;
  };

  /* ---------- session ---------- */

  Widget.prototype.getSession = function () {
    var self = this;
    if (self.token) return Promise.resolve(self.token);

    var stored = null;
    try { stored = window.sessionStorage.getItem(self.storageKey); } catch (e) { /* storage blocked */ }
    if (stored) {
      self.token = stored;
      return Promise.resolve(stored);
    }

    if (!self.sessionPromise) {
      self.sessionPromise = apiJson(API_BASE + '/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'PRODUCT',
          productHandle: self.productHandle,
          productTitle: self.productTitle
        })
      }).then(function (data) {
        if (!data || !data.token) throw new Error('No session token');
        self.token = data.token;
        try { window.sessionStorage.setItem(self.storageKey, data.token); } catch (e) { /* noop */ }
        self.sessionPromise = null;
        return self.token;
      }).catch(function (err) {
        self.sessionPromise = null;
        throw err;
      });
    }
    return self.sessionPromise;
  };

  Widget.prototype.clearSession = function () {
    this.token = null;
    this.sessionPromise = null;
    try { window.sessionStorage.removeItem(this.storageKey); } catch (e) { /* noop */ }
  };

  /* ---------- file intake ---------- */

  Widget.prototype.handleFiles = function (ctx, fileList) {
    var self = this;
    var field = ctx.field;
    ctx.errEl.textContent = '';

    var limit = field.allowMultiple === false ? 1 : (field.maxFiles > 0 ? field.maxFiles : Infinity);
    var incoming = Array.prototype.slice.call(fileList);
    var current = this.fieldCounts[field.id] || 0;
    var room = limit - current;

    if (room <= 0) {
      ctx.errEl.textContent = limit === 1
        ? 'Only one file allowed — remove the existing file first.'
        : 'Maximum of ' + limit + ' files reached — remove a file first.';
      return;
    }
    if (incoming.length > room) {
      ctx.errEl.textContent = 'Only ' + room + ' more file' + (room === 1 ? '' : 's') + ' allowed; extra files were skipped.';
      incoming = incoming.slice(0, room);
    }

    incoming.forEach(function (file) {
      self.fieldCounts[field.id] = (self.fieldCounts[field.id] || 0) + 1;
      // Serialize processing so crop modals appear one at a time
      self.queue = self.queue.then(function () {
        return self.processFile(ctx, file).catch(function () { /* handled per-file */ });
      });
    });
  };

  Widget.prototype.processFile = function (ctx, file) {
    var self = this;
    var field = ctx.field;

    var basicError = validateFile(field, file);
    if (basicError) {
      ctx.errEl.textContent = file.name + ': ' + basicError;
      self.fieldCounts[field.id] = Math.max(0, (self.fieldCounts[field.id] || 1) - 1);
      return Promise.resolve();
    }

    var image = isImageFile(file);
    var dimsPromise = image ? getImageDimensions(file) : Promise.resolve(null);

    return dimsPromise.then(function (dims) {
      if (image) {
        var dimError = validateDimensions(field, dims);
        if (dimError) {
          ctx.errEl.textContent = file.name + ': ' + dimError;
          self.fieldCounts[field.id] = Math.max(0, (self.fieldCounts[field.id] || 1) - 1);
          return;
        }
      }

      var cropPromise = (field.cropEnabled && image)
        ? openCropModal(field, file)
        : Promise.resolve({ action: 'skip' });

      return cropPromise.then(function (result) {
        if (result.action === 'cancel') {
          self.fieldCounts[field.id] = Math.max(0, (self.fieldCounts[field.id] || 1) - 1);
          return;
        }

        var blob = file;
        var filename = file.name;
        if (result.action === 'apply' && result.blob) {
          blob = result.blob;
          filename = result.filename;
          // dimensions changed by crop — re-measure
          return getImageDimensions(blob).then(function (croppedDims) {
            return self.startUpload(ctx, blob, filename, croppedDims, true);
          });
        }
        return self.startUpload(ctx, blob, filename, dims, image);
      });
    });
  };

  Widget.prototype.startUpload = function (ctx, blob, filename, dims, isImage) {
    var self = this;
    var field = ctx.field;
    var card = createFileCard(ctx.list, filename, blob.size);
    var thumbObjectUrl = null;

    // Thumbnail + optional preview blob (PDF/AI page 1 rendered via pdf.js)
    var previewPromise;
    if (isImage) {
      try {
        thumbObjectUrl = URL.createObjectURL(blob);
        card.setThumb(thumbObjectUrl);
      } catch (e) { /* keep ext label */ }
      previewPromise = Promise.resolve(null);
    } else if (isPdfLike(blob)) {
      previewPromise = renderPdfPreview(blob)
        .then(function (res) {
          if (res && res.thumbUrl) card.setThumb(res.thumbUrl);
          return res ? res.previewBlob : null;
        })
        .catch(function () {
          // Some .ai files aren't PDF-compatible — generic icon, no preview part
          return null;
        });
    } else {
      previewPromise = Promise.resolve(null);
    }

    self.setPending(1);

    var uploadedFileId = null;
    card.removeBtn.addEventListener('click', function () {
      if (uploadedFileId) {
        self.removeFile(ctx, uploadedFileId, card);
      } else {
        // error-state card (or still uploading — button disabled during upload)
        card.destroy();
        if (thumbObjectUrl) URL.revokeObjectURL(thumbObjectUrl);
      }
    });
    card.setBusy(true);

    return previewPromise
      .then(function (previewBlob) {
        return self.getSession().then(function (token) {
          return self.xhrUpload(token, field, blob, filename, dims, previewBlob, card).catch(function (err) {
            // Stale/expired session from a previous visit — start fresh once
            if (err && (err.status === 401 || err.status === 403 || err.status === 404 || err.status === 410)) {
              self.clearSession();
              return self.getSession().then(function (fresh) {
                return self.xhrUpload(fresh, field, blob, filename, dims, previewBlob, card);
              });
            }
            throw err;
          });
        });
      })
      .then(function (resp) {
        var f = (resp && resp.file) || {};
        uploadedFileId = f.id;
        self.files.push({
          id: f.id,
          fieldId: field.id,
          name: f.originalFilename || f.name || filename,
          size: f.sizeBytes || blob.size
        });
        card.setDone();
        card.setBusy(false);
        self.updateProperties();
      })
      .catch(function (err) {
        card.setError((err && err.message) ? 'Upload failed — ' + err.message : 'Upload failed. Please try again.');
        card.setBusy(false);
        self.fieldCounts[field.id] = Math.max(0, (self.fieldCounts[field.id] || 1) - 1);
      })
      .then(function () {
        self.setPending(-1); // runs on both success and failure (re-enables add-to-cart)
      });
  };

  Widget.prototype.xhrUpload = function (token, field, blob, filename, dims, previewBlob, card) {
    return new Promise(function (resolve, reject) {
      var fd = new FormData();
      fd.append('file', blob, filename);
      fd.append('fieldId', String(field.id));
      if (dims && dims.width > 0) {
        fd.append('width', String(dims.width));
        fd.append('height', String(dims.height));
      }
      if (previewBlob) fd.append('preview', previewBlob, 'preview.png');

      var xhr = new XMLHttpRequest();
      xhr.open('POST', API_BASE + '/sessions/' + encodeURIComponent(token) + '/files');
      xhr.responseType = 'json';
      xhr.setRequestHeader('Accept', 'application/json');
      if (xhr.upload) {
        xhr.upload.onprogress = function (e) {
          if (e.lengthComputable) card.setProgress(e.loaded / e.total);
        };
      }
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr.response || {});
        } else {
          var err = new Error('server responded ' + xhr.status);
          err.status = xhr.status;
          reject(err);
        }
      };
      xhr.onerror = function () { reject(new Error('network error')); };
      xhr.onabort = function () { reject(new Error('upload cancelled')); };
      xhr.send(fd);
    });
  };

  Widget.prototype.removeFile = function (ctx, fileId, card) {
    var self = this;
    card.setBusy(true);
    self.getSession()
      .then(function (token) {
        return window.fetch(API_BASE + '/sessions/' + encodeURIComponent(token) + '/files/' + encodeURIComponent(fileId), {
          method: 'DELETE',
          headers: { Accept: 'application/json' }
        });
      })
      .catch(function () { /* best-effort delete; still remove locally */ })
      .then(function () {
        self.files = self.files.filter(function (f) { return f.id !== fileId; });
        self.fieldCounts[ctx.field.id] = Math.max(0, (self.fieldCounts[ctx.field.id] || 1) - 1);
        card.destroy();
        self.updateProperties();
      });
  };

  /* ---------- line item properties + submit gating ---------- */

  Widget.prototype.setHiddenInput = function (name, value) {
    if (!this.form) return;
    var selector = 'input[data-fu-property][name="' + name.replace(/"/g, '\\"') + '"]';
    var input = this.form.querySelector(selector);
    if (!value) {
      if (input) input.parentNode.removeChild(input);
      return;
    }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.setAttribute('data-fu-property', '');
      this.form.appendChild(input);
    }
    input.value = value;
  };

  Widget.prototype.updateProperties = function () {
    var ids = this.files.map(function (f) { return f.id; }).filter(Boolean);
    var names = this.files.map(function (f) { return f.name; }).filter(Boolean);
    this.setHiddenInput('properties[_upload_session]', ids.length ? (this.token || '') : '');
    this.setHiddenInput('properties[_upload_files]', ids.join(','));
    this.setHiddenInput('properties[Uploaded Files]', names.join(', '));
  };

  Widget.prototype.setPending = function (delta) {
    this.pendingUploads = Math.max(0, this.pendingUploads + delta);
    if (!this.form) return;
    var busy = this.pendingUploads > 0;
    var buttons = this.form.querySelectorAll(
      'button[type="submit"], input[type="submit"], button[name="add"], [data-add-to-cart]'
    );
    Array.prototype.forEach.call(buttons, function (btn) {
      if (busy) {
        if (!btn.disabled) {
          btn.setAttribute('data-fu-disabled', '1');
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
        }
      } else if (btn.getAttribute('data-fu-disabled')) {
        btn.disabled = false;
        btn.removeAttribute('data-fu-disabled');
        btn.removeAttribute('aria-busy');
      }
    });
  };

  /* ------------------------------------------------------------------ *
   * Bootstrap
   * ------------------------------------------------------------------ */

  var instances = [];

  function initAll(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var nodes = scope.querySelectorAll('[data-foundry-upload]:not([data-fu-initialized])');
    Array.prototype.forEach.call(nodes, function (node) {
      node.setAttribute('data-fu-initialized', '');
      // The theme redraws the product form when another variant is picked
      // (Empire does), bringing a fresh, empty container. Put the shopper's
      // existing upload box back — files, progress and all — instead of
      // starting over (or, as before, showing nothing).
      var handle = node.getAttribute('data-fu-handle') || node.getAttribute('data-product-handle') || '';
      for (var i = 0; i < instances.length; i++) {
        var w = instances[i];
        if (w.productHandle === handle && !document.documentElement.contains(w.container)) {
          try {
            node.parentNode.replaceChild(w.container, node);
            w.form = w.findForm();
            w.updateProperties();
            return;
          } catch (e) { /* fall through to a fresh widget */ }
        }
      }
      try {
        instances.push(new Widget(node));
      } catch (e) {
        /* never break the product page */
      }
    });
  }

  // Containers that arrive after page load (a redrawn product form).
  var fuScanQueued = false;
  try {
    new MutationObserver(function (muts) {
      if (fuScanQueued) return;
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].addedNodes && muts[i].addedNodes.length) {
          fuScanQueued = true;
          setTimeout(function () { fuScanQueued = false; initAll(document); }, 0);
          return;
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  } catch (e) { /* old browser: page-load init only, as before */ }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Re-init when Shopify theme editor reloads a section
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target || document);
  });

  window.FoundryUpload = {
    __loaded: true,
    version: '1.1.0',
    base: FOUNDRY_BASE,
    instances: instances,
    init: initAll
  };
})();
